// src/pages/student/OnlineClass.js
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Alert, InputGroup, Dropdown
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where,
} from "firebase/firestore";
import { db } from "../../services/firebase";

// ⬇️ NEW: virtualization
// npm i react-window react-virtualized-auto-sizer
import { FixedSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtAt = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—";
};
const within = (d, now = new Date()) => {
  const js = toJS(d);
  return js && js >= dayjs(now).startOf("day").toDate();
};
const uniqBy = (arr, key = (x) => x.id) => {
  const m = new Map();
  for (const x of arr) m.set(key(x), x);
  return [...m.values()];
};
const norm = (s) => String(s || "").trim().toLowerCase();

const showIndexLink = (err) => {
  const url = err?.message?.match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i)?.[0];
  if (url) {
    toast.info(
      <a href={url} target="_blank" rel="noreferrer">Create required Firestore index</a>,
      { autoClose: 9000 }
    );
  }
};

/* ------- theme helpers ------- */
const ACCENTS = [
  "#0d6efd","#3b82f6","#1d4ed8","#60a5fa",
  "#dc3545","#ef4444","#991b1b","#f87171",
  "#198754","#22c55e","#065f46","#86efac",
  "#ffc107","#facc15","#d97706","#fde047",
  "#6f42c1","#8b5cf6","#7e22ce","#c4b5fd",
  "#20c997","#06b6d4","#164e63","#67e8f9",
  "#fd7e14","#f97316","#9a3412","#fb923c",
  "#6c757d","#94a3b8","#334155","#e2e8f0",
  "#000000","#ffffff",
];
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};
const hexEq = (a, b) => normalizeHex6(a) === normalizeHex6(b);

/** Subject -> Bootstrap Icons mapping */
const SUBJECT_ICONS = {
  physics: "bi-lightning-charge",
  chemistry: "bi-beaker",
  biology: "bi-heart-pulse",
  zoology: "bi-bug",
  botany: "bi-flower3",
  maths: "bi-calculator",
  mathematics: "bi-calculator",
  algebra: "bi-calculator",
  geometry: "bi-rulers",
  statistics: "bi-bar-chart",
  social: "bi-people",
  "social science": "bi-people",
  history: "bi-hourglass-split",
  geography: "bi-geo-alt",
  civics: "bi-bank",
  politics: "bi-bank",
  computers: "bi-pc-display",
  "computer science": "bi-code-slash",
  coding: "bi-code-slash",
  english: "bi-book",
  hindi: "bi-translate",
  sanskrit: "bi-translate",
  tamil: "bi-translate",
  telugu: "bi-translate",
  kannada: "bi-translate",
  marathi: "bi-translate",
  french: "bi-translate",
  german: "bi-translate",
  arts: "bi-palette",
  drawing: "bi-brush",
  music: "bi-music-note-beamed",
  dance: "bi-music-note-list",
  pe: "bi-trophy",
  sports: "bi-trophy",
  economics: "bi-graph-up",
  commerce: "bi-cash-coin",
  accounts: "bi-journal-richtext",
  science: "bi-magnet",
  robotics: "bi-robot",
  default: "bi-journal-text",
};
const subjectIcon = (subject) => SUBJECT_ICONS[norm(subject)] || SUBJECT_ICONS.default;

/* ------- audience matchers (section + branch) ------- */
const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** Match section audience: supports `section`, `sections:[]`, or "all" */
const matchesSection = (item, studentSection) => {
  if (!studentSection) return true;
  const s = String(studentSection).trim();
  if (Array.isArray(item.sections) && item.sections.length) {
    return item.sections.some((x) => same(x, s)) || item.sections.some((x) => same(x, "all"));
  }
  if (item.section) {
    return same(item.section, s) || same(item.section, "all");
  }
  return true; // no section constraint
};

/** Match branch audience: supports `branch`, `branches:[]`, or "all" */
const matchesBranch = (item, studentBranch) => {
  const b = String(studentBranch || "").trim();
  if (!b) return true;
  if (Array.isArray(item.branches) && item.branches.length) {
    return item.branches.some((x) => same(x, b)) || item.branches.some((x) => same(x, "all"));
  }
  if (item.branch) {
    return same(item.branch, b) || same(item.branch, "all");
  }
  return true; // no branch constraint
};

/** Master audience matcher (className is enforced by the query) */
const matchesAudience = (item, profile) =>
  matchesSection(item, profile?.section) && matchesBranch(item, profile?.branch);

/* ───────────────── Theme dropdown (stateless) ───────────────── */
function AccentDropdown({ active, onPick, hexToRgba }) {
  const activeHex = normalizeHex6(active);
  return (
    <Dropdown>
      <Dropdown.Menu style={{ padding: 10, maxHeight: 240, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 24px)", gap: 8, minWidth: 230 }}>
          {ACCENTS.map((c) => {
            const col = normalizeHex6(c);
            const activeRing = hexEq(col, activeHex);
            return (
              <button
                key={col}
                type="button"
                title={col}
                onClick={() => onPick && onPick(col)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: col,
                  border: "none", outline: "none", cursor: "pointer",
                  transition: "box-shadow .2s, transform .15s",
                  boxShadow: activeRing
                    ? `0 0 0 2px rgba(0,0,0,.35), 0 0 0 4px ${hexToRgba(col, .4)}`
                    : `0 0 0 1px rgba(0,0,0,.2)`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            );
          })}
          <label
            style={{
              width: 20, height: 20, borderRadius: "50%", cursor: "pointer",
              border: "1px dashed rgba(0,0,0,.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "#666",
            }}
            title="Custom color"
          >
            +
            <input type="color" style={{ display: "none" }} onChange={(e) => onPick && onPick(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function OnlineClass() {
  const brand = useBrand();
  const { primary, accent, setPrimary, setAccent } = brand || {};

  // normalized theme
  const primaryColor = useMemo(() => normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentComputed = useMemo(
    () => normalizeHex6(accent || primaryColor),
    [accent, primaryColor]
  );

  // push to :root for global CSS vars
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryColor);
    root.style.setProperty("--brand-accent", accentComputed);
  }, [primaryColor, accentComputed]);

  const onPickTheme = useCallback((c) => {
    const col = normalizeHex6(c);
    setPrimary?.(col);
    setAccent?.(col);
  }, [setPrimary, setAccent]);

  // IDs from login
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // student profile to get class/section/branch
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // classes
  const [items, setItems] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);

  // teacher/host cache (id -> {displayName, photoURL, ...})
  const [people, setPeople] = useState({});

  // filters
  const [onlyUpcoming, setOnlyUpcoming] = useState(true);
  const [q, setQ] = useState("");
  const [fSubject, setFSubject] = useState("all");
  const [fTeacher, setFTeacher] = useState("all");
  const [fStatus, setFStatus] = useState("all"); // all | live | upcoming | ended
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // particles init
  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  // guard + load profile
  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.");
      return;
    }
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student profile.");
        } else {
          setProfile({ id: snap.id, ...snap.data() });
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error("Failed to load profile.");
      })
      .finally(() => setLoadingProfile(false));
  }, [schoolId, studentId]);

  // subscribe to classes by className & section (+ optional branch)
  useEffect(() => {
    if (!schoolId || !profile?.className) return;
    if (norm(profile?.status) === "inactive") {
      setItems([]);
      setLoadingClasses(false);
      return;
    }

    setLoadingClasses(true);
    const col = collection(db, "schools", schoolId, "onlineClasses");

    // keep queries simple; sort + merge in-memory
    const q1 =
      profile.section
        ? query(
            col,
            where("className", "==", profile.className),
            where("sections", "array-contains", profile.section)
          )
        : null;

    const q2 =
      profile.section
        ? query(
            col,
            where("className", "==", profile.className),
            where("section", "in", [profile.section, "all"]) // may need index
          )
        : query(col, where("className", "==", profile.className));

    const unsubs = [];

    if (q1) {
      const u1 = onSnapshot(
        q1,
        (snap) => {
          const A = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setItems((prev) => {
            const merged = uniqBy([...A, ...prev]);
            return merged.sort((a, b) => (toJS(a.startAt) - toJS(b.startAt)));
          });
          setLoadingClasses(false);
        },
        (err) => {
          console.error(err);
          showIndexLink(err);
          setLoadingClasses(false);
        }
      );
      unsubs.push(u1);
    }

    const u2 = onSnapshot(
      q2,
      (snap) => {
        const B = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems((prev) => {
          const merged = uniqBy([...prev, ...B]);
          return merged.sort((a, b) => (toJS(a.startAt) - toJS(b.startAt)));
        });
        setLoadingClasses(false);
      },
      (err) => {
        console.error(err);
        showIndexLink(err);
        toast.error("Failed to load online classes.");
        setLoadingClasses(false);
      }
    );
    unsubs.push(u2);

    // OPTIONAL: branch-targeted subscription
    if (profile.branch) {
      try {
        const q3 = query(
          col,
          where("className", "==", profile.className),
          where("branches", "array-contains", profile.branch)
        );
        const u3 = onSnapshot(
          q3,
          (snap) => {
            const C = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setItems((prev) => {
              const merged = uniqBy([...prev, ...C]);
              return merged.sort((a, b) => (toJS(a.startAt) - toJS(b.startAt)));
            });
            setLoadingClasses(false);
          },
          (err) => {
            console.error(err);
            showIndexLink(err);
          }
        );
        unsubs.push(u3);
      } catch (e) {
        // index link toast already shown if needed
      }
    }

    return () => unsubs.forEach((u) => u && u());
  }, [schoolId, profile?.className, profile?.section, profile?.status, profile?.branch]);

  // Load teacher/host profiles for class items (robust: by doc id, then by uid/email)
  useEffect(() => {
    if (!schoolId || items.length === 0) return;

    const ids = Array.from(
      new Set(
        items.flatMap((it) =>
          [it.teacherId, it.hostId].filter(Boolean).map((v) => String(v).trim())
        )
      )
    );

    const missing = ids.filter((id) => !(id in people));
    if (missing.length === 0) return;

    const collections = ["teachers", "staff", "users"];

    const loadOne = async (id) => {
      for (const coll of collections) {
        try {
          const refDoc = doc(db, "schools", schoolId, coll, id);
          const snap = await getDoc(refDoc);
          if (snap.exists()) return { id: snap.id, ...snap.data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q1 = query(collection(db, "schools", schoolId, coll), where("uid", "==", id));
          const qs = await getDocs(q1);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      for (const coll of collections) {
        try {
          const q2 = query(collection(db, "schools", schoolId, coll), where("email", "==", id));
          const qs = await getDocs(q2);
          if (!qs.empty) return { id: qs.docs[0].id, ...qs.docs[0].data() };
        } catch {}
      }
      return null;
    };

    (async () => {
      const entries = await Promise.all(missing.map(async (id) => [id, await loadOne(id)]));
      const next = Object.fromEntries(entries);
      if (Object.keys(next).length) setPeople((p) => ({ ...p, ...next }));
    })();
  }, [schoolId, items, people]);

  /* ---------- classify helpers ---------- */
  const classifyClass = (cl) => {
    const now = new Date();
    const start = toJS(cl.startAt);
    const end = toJS(cl.endAt);
    const status = norm(cl.status);
    const live = status === "live" || (start && end && now >= start && now <= end);
    const ended = status === "completed" || (end && now > end);
    const upcoming = !live && !ended && start && now < start;
    return { live, ended, upcoming };
  };

  /* ---------- derived filter options ---------- */
  const subjectOptions = useMemo(() => {
    const s = new Set();
    items.forEach((it) => {
      const sub = (it.subject || it.title || "").trim();
      if (sub) s.add(sub);
    });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const teacherOptions = useMemo(() => {
    const names = new Set();
    const getName = (cl) => {
      const tKey = cl.teacherId ? String(cl.teacherId).trim() : "";
      const hostKey = cl.hostId ? String(cl.hostId).trim() : "";
      const teacher = tKey ? people[tKey] : null;
      const host = hostKey ? people[hostKey] : null;
      const teacherName =
        teacher?.displayName ||
        teacher?.name ||
        [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
        teacher?.fullName ||
        cl.teacherName ||
        cl.instructor ||
        (typeof cl.teacher === "string" ? cl.teacher : "") ||
        "";
      const hostName =
        host?.displayName ||
        host?.name ||
        [host?.firstName, host?.lastName].filter(Boolean).join(" ") ||
        host?.fullName ||
        cl.hostName ||
        (typeof cl.meetingHost === "string" ? cl.meetingHost.trim() : "") ||
        "";
      if (teacherName) names.add(teacherName);
      if (hostName) names.add(hostName);
    };
    items.forEach(getName);
    return ["all", ...Array.from(names).sort((a, b) => a.localeCompare(b))];
  }, [items, people]);

  /* ---------- filtered list ---------- */
  const filtered = useMemo(() => {
    const base = items.filter((it) => it && it.startAt);

    const now = new Date();
    return base
      .filter((it) => matchesAudience(it, profile))
      .filter((it) => (onlyUpcoming ? within(it.startAt, now) : true))
      .filter((it) => {
        const { live, ended, upcoming } = classifyClass(it);
        if (fStatus === "live" && !live) return false;
        if (fStatus === "upcoming" && !upcoming) return false;
        if (fStatus === "ended" && !ended) return false;

        if (fSubject !== "all") {
          const sub = (it.subject || it.title || "").trim();
          if (sub !== fSubject) return false;
        }

        if (fTeacher !== "all") {
          const tKey = it.teacherId ? String(it.teacherId).trim() : "";
          const hKey = it.hostId ? String(it.hostId).trim() : "";
          const teacher = tKey ? people[tKey] : null;
          const host = hKey ? people[hKey] : null;
          const teacherName =
            teacher?.displayName ||
            teacher?.name ||
            [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
            teacher?.fullName ||
            it.teacherName ||
            it.instructor ||
            (typeof it.teacher === "string" ? it.teacher : "") ||
            "";
          const hostName =
            host?.displayName ||
            host?.name ||
            [host?.firstName, host?.lastName].filter(Boolean).join(" ") ||
            host?.fullName ||
            it.hostName ||
            (typeof it.meetingHost === "string" ? it.meetingHost.trim() : "");
          if (teacherName !== fTeacher && hostName !== fTeacher) return false;
        }

        const s = toJS(it.startAt);
        if (fromDate && dayjs(s).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && dayjs(s).isAfter(dayjs(toDate).endOf("day"))) return false;

        if (q.trim()) {
          const hay = [
            it.title, it.subject, it.notes,
            it.className, it.section,
            ...(Array.isArray(it.sections) ? it.sections : []),
            it.branch,
            ...(Array.isArray(it.branches) ? it.branches : []),
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          if (!hay.includes(q.trim().toLowerCase())) return false;
        }

        return true;
      })
      .sort((a, b) => (toJS(a.startAt) - toJS(b.startAt)));
  }, [
    items, profile, onlyUpcoming, fStatus, fSubject, fTeacher,
    fromDate, toDate, q, people
  ]);

  const classTag = useMemo(
    () => [profile?.className, profile?.section].filter(Boolean).join("-") || "—",
    [profile?.className, profile?.section]
  );

  const onJoin = (url, state = {}) => {
    if (!url) return toast.info("Meeting link not available.");
    if (state.ended) return toast.info("This class has ended. Link is disabled.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const statusBadge = (s) => {
    const v = norm(s);
    if (v === "live") return <Badge bg="danger">Live</Badge>;
    if (v === "completed") return <Badge bg="secondary">Completed</Badge>;
    return <Badge bg="info" text="dark">Scheduled</Badge>;
  };

  const isActive = norm(profile?.status) !== "inactive";

  /* ---------------- Virtualized Grid ---------------- */
  const gridRef = useRef(null);

  const renderCard = (cl) => {
    const end = toJS(cl.endAt);
    const { live, ended } = classifyClass(cl);

    const tKey = cl.teacherId ? String(cl.teacherId).trim() : "";
    const hKey = cl.hostId ? String(cl.hostId).trim() : "";
    const teacher = tKey ? people[tKey] : null;
    const host = hKey ? people[hKey] : null;

    const teacherName =
      teacher?.displayName ||
      teacher?.name ||
      [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
      teacher?.fullName ||
      cl.teacherName ||
      cl.instructor ||
      (typeof cl.teacher === "string" ? cl.teacher : "") ||
      "—";

    const hostName =
      host?.displayName ||
      host?.name ||
      [host?.firstName, host?.lastName].filter(Boolean).join(" ") ||
      host?.fullName ||
      cl.hostName ||
      (typeof cl.meetingHost === "string" ? cl.meetingHost.trim() : "");

    const teacherPhoto = teacher?.photoURL || teacher?.avatarUrl || teacher?.avatar || teacher?.photo;
    const hostPhoto = host?.photoURL || host?.avatarUrl || host?.avatar || host?.photo;

    const subj = cl.subject || cl.title || "Class";
    const ico = subjectIcon(subj);

    const secTxt =
      cl.section
        ? `-${cl.section}`
        : Array.isArray(cl.sections) && cl.sections.length
        ? `-${cl.sections.join(",")}`
        : "";
    const branchTxt =
      cl.branch
        ? ` • ${cl.branch}`
        : Array.isArray(cl.branches) && cl.branches.length
        ? ` • ${cl.branches.join(",")}`
        : "";

    return (
      <Card
        className={`h-100 shadow-sm border-0 online-class-card ${ended ? "ended" : live ? "live" : "scheduled"}`}
        style={{ borderColor: hexToRgba(primaryColor, 0.22), position: "relative", overflow: "hidden" }}
      >
        <div className="card-particles" aria-hidden />
        <Card.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="d-flex align-items-center gap-3">
              <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={subj}>
                <i className={`bi ${ico}`} />
              </div>
              <div className="fw-semibold text-truncate" title={cl.title || subj || "Session"}>
                {cl.title || subj || "Session"}
              </div>
            </div>
            {statusBadge(live ? "live" : ended ? "completed" : "scheduled")}
          </div>

          <div className="text-muted small mb-1">
            Subject: <span className="fw-semibold">{subj}</span>
          </div>

          <div className="small d-flex align-items-center gap-2">
            {teacherPhoto ? (
              <img
                src={teacherPhoto}
                alt={teacherName}
                className="rounded-circle border"
                style={{ width: 24, height: 24, objectFit: "cover" }}
              />
            ) : (
              <i className="bi bi-person-badge" />
            )}
            <span className="text-muted">Teacher:</span>
            <span className="fw-semibold text-truncate">{teacherName}</span>
          </div>

          {hostName ? (
            <div className="small d-flex align-items-center gap-2 mt-1">
              {hostPhoto ? (
                <img
                  src={hostPhoto}
                  alt={hostName}
                  className="rounded-circle border"
                  style={{ width: 22, height: 22, objectFit: "cover" }}
                />
              ) : (
                <i className="bi bi-person-video2" />
              )}
              <span className="text-muted">Host:</span>
              <span className="fw-semibold text-truncate">{hostName}</span>
            </div>
          ) : null}

          <div className="text-muted small mt-2">
            Time: <span className="fw-semibold">{fmtAt(cl.startAt)}</span>
            {end ? <> &nbsp;–&nbsp; <span className="fw-semibold">{fmtAt(cl.endAt)}</span></> : null}
          </div>

          {cl.notes ? (
            <div className="small mt-2 text-truncate" title={cl.notes}>{cl.notes}</div>
          ) : null}

          <div className="mt-auto pt-3 d-flex justify-content-between align-items-center">
            <Badge bg="light" text="dark" className="border">
              {(cl.className || "—")}{secTxt}{branchTxt}
            </Badge>
            <Button
              size="sm"
              className={`btn-neo ${ended ? "disabled" : ""}`}
              disabled={ended}
              onClick={() => onJoin(cl.meetLink, { ended })}
            >
              <i className="bi bi-camera-video me-1" />
              {ended ? "Ended" : live ? "Join now" : "Open link"}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Builds a 1/2/3-column grid based on container width (virtualized)
  const VirtualizedGrid = ({ data }) => (
    <div style={{ height: "70vh" }}>
      <AutoSizer>
        {({ width, height }) => {
          const gutter = 12; // px
          const cols = width < 576 ? 1 : width < 992 ? 2 : 3; // xs, md, lg
          const colWidth = Math.floor((width - gutter * (cols + 1)) / cols);
          const rowHeight = 300; // approx card height
          const rowCount = Math.ceil(data.length / cols);

          return (
            <Grid
              ref={gridRef}
              columnCount={cols}
              columnWidth={colWidth + gutter}
              height={height}
              rowCount={rowCount}
              rowHeight={rowHeight + gutter}
              width={width}
              overscanRowCount={4}
            >
              {({ columnIndex, rowIndex, style }) => {
                const index = rowIndex * cols + columnIndex;
                if (index >= data.length) return <div style={style} />;
                const cl = data[index];
                return (
                  <div style={{
                    ...style,
                    left: style.left + gutter,
                    top: style.top + gutter,
                    width: colWidth,
                    height: rowHeight,
                  }}>
                    {renderCard(cl)}
                  </div>
                );
              }}
            </Grid>
          );
        }}
      </AutoSizer>
    </div>
  );

  return (
    <div className="position-relative overflow-hidden">
      {/* FULL-PAGE background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primaryColor, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(accentComputed,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="online-class-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 70, density: { enable: true, area: 900 } },
            color: { value: [primaryColor, accentComputed] },
            links: { enable: true, color: hexToRgba(primaryColor, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.1, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 40 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 24 } } } },
          ],
        }}
      />

      {/* Foreground */}
      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer
          position="bottom-right"
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          theme="dark"
        />

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex flex-column">
            <h5 className="mb-0">Online Classes</h5>
            <small className="text-muted">
              {studentName || "Student"} • Class { [profile?.className, profile?.section].filter(Boolean).join("-") || "—" }
              {profile?.branch ? ` • ${profile.branch}` : ""}
              {norm(profile?.status) === "inactive" && " • Inactive"}
            </small>
          </div>

          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="switch"
              id="upcoming-switch"
              label="Only upcoming"
              checked={onlyUpcoming}
              onChange={(e) => setOnlyUpcoming(e.target.checked)}
              disabled={norm(profile?.status) === "inactive"}
            />
            <AccentDropdown active={primaryColor} onPick={onPickTheme} hexToRgba={hexToRgba} />
          </div>
        </div>

        {/* Filter Bar */}
        {norm(profile?.status) !== "inactive" && (
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body className="py-3">
              <Row className="g-2">
                <Col xs={12} md={4} lg={3}>
                  <InputGroup>
                    <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                    <Form.Control
                      placeholder="Search title / subject / notes"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </InputGroup>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                    {subjectOptions.map((s) => (
                      <option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fTeacher} onChange={(e) => setFTeacher(e.target.value)}>
                    {teacherOptions.map((t) => (
                      <option key={t} value={t}>{t === "all" ? "All teachers/hosts" : t}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={2}>
                  <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="live">Live</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="ended">Ended</option>
                  </Form.Select>
                </Col>

                <Col xs={6} md={4} lg={1}>
                  <Form.Control
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    title="From date"
                  />
                </Col>
                <Col xs={6} md={4} lg={1}>
                  <Form.Control
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    title="To date"
                  />
                </Col>

                <Col xs={12} md={3} lg={1} className="d-grid">
                  <Button
                    className="btn-ghost"
                    onClick={() => {
                      setQ(""); setFSubject("all"); setFTeacher("all");
                      setFStatus("all"); setFromDate(""); setToDate("");
                    }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Inactive notice */}
        {!loadingProfile && norm(profile?.status) === "inactive" ? (
          <Alert variant="warning" className="mb-3">
            Your student account is currently <b>inactive</b>. Please contact your school
            management to activate your account to access online classes.
          </Alert>
        ) : null}

        {/* List / Loading */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            {loadingProfile ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading…
              </div>
            ) : norm(profile?.status) === "inactive" ? (
              <div className="text-muted py-4 text-center">
                Online classes are hidden until your account is activated.
              </div>
            ) : loadingClasses ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" /> Loading classes…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-muted py-4 text-center">No classes found.</div>
            ) : (
              <VirtualizedGrid data={filtered} />
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Styles */}
      <style>{`
        .online-class-card {
          border-radius: 18px;
          background: linear-gradient(
            180deg,
            ${hexToRgba(primaryColor, 0.06)} 0%,
            transparent 60%
          );
          transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease, border-color .18s ease;
        }
        .online-class-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 32px ${hexToRgba(primaryColor, .14)};
          border-color: ${hexToRgba(primaryColor, .28)};
        }

        /* State tints */
        .online-class-card.live { box-shadow: 0 .35rem .9rem ${hexToRgba(accentComputed, .18)}; }
        .online-class-card.ended { opacity: .7; filter: grayscale(.12); }

        /* Bigger subject icon */
        .subj-ico {
          width: 64px; height: 64px; border-radius: 16px;
          background: ${hexToRgba(primaryColor, 0.18)};
          color: ${primaryColor};
          border: 1px solid ${hexToRgba(primaryColor, 0.30)};
          font-size: 34px;
        }
        @media (max-width: 576px) {
          .subj-ico { width: 54px; height: 54px; font-size: 28px; }
        }

        /* Per-card animated particle overlay (pure CSS, light) */
        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(${hexToRgba(primaryColor, .18)} 1px, transparent 1px),
            radial-gradient(${hexToRgba(accentComputed, .18)} 1px, transparent 1px);
          background-position: 0 0, 12px 12px;
          background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite;
          opacity: .22;
        }
        .online-class-card.live .card-particles { animation-duration: 6s; opacity: .28; }
        .online-class-card.ended .card-particles { animation-duration: 14s; opacity: .14; }

        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
        }

        /* Toast styles */
        .Toastify__toast {
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid ${hexToRgba(accentComputed, 0.4)};
          background: linear-gradient(
            135deg,
            ${hexToRgba(primaryColor, 0.15)} 0%,
            ${hexToRgba(accentComputed, 0.15)} 100%
          );
          backdrop-filter: blur(6px);
        }
        .Toastify__toast-body { color: ${accentComputed}; font-weight: 500; }
        .Toastify__toast--info { border-left: 4px solid ${primaryColor}; }
        .Toastify__toast--success { border-left: 4px solid #28a745; color: #28a745; }
        .Toastify__toast--error { border-left: 4px solid #dc3545; color: #ff6b6b; }
        .Toastify__toast--warning { border-left: 4px solid #ffc107; color: #ffc107; }
        .Toastify__progress-bar { background: ${accentComputed}; }

        /* Gradient primary action (neo) */
        .btn-neo{
          --_bg1: ${hexToRgba(primaryColor, .95)};
          --_bg2: ${hexToRgba(accentComputed, .95)};
          color:#fff !important;
          background: linear-gradient(135deg, var(--_bg1) 0%, var(--_bg2) 100%);
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          box-shadow: 0 10px 18px ${hexToRgba(primaryColor,.24)};
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
        }
        .btn-neo:hover{
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow: 0 14px 26px ${hexToRgba(primaryColor,.30)};
        }
        .btn-neo:active{
          transform: translateY(0);
          filter: brightness(.98);
          box-shadow: 0 6px 14px ${hexToRgba(primaryColor,.24)} inset;
        }
        .btn-neo.disabled, .btn-neo:disabled {
          opacity: .65 !important;
          filter: grayscale(.2);
          box-shadow: none;
        }

        /* Ghost button for secondary actions */
        .btn-ghost{
          color: ${primaryColor} !important;
          background: transparent;
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease, color .12s ease;
        }
        .btn-ghost:hover{
          background: ${hexToRgba(primaryColor,.10)};
          box-shadow: 0 6px 16px ${hexToRgba(primaryColor,.18)};
          transform: translateY(-1px);
        }

      `}</style>
    </div>
  );
}
